import React, {
    useEffect, useRef, useState, useCallback,
} from 'react';
import router from 'next/router';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    Field, Form, Formik, FormikProps,
} from 'formik';
import {
    Button, Label, Modal, Spinner,
} from 'flowbite-react';
import { useRecoilValue } from 'recoil';

import { handleValidationErrors, onCompletedHandler, onErrorHandler } from '@/services/apollo';
import {
    CREATE_WORK_HISTORY,
    FETCH_WORK_HISTORY,
    DELETE_WORK_HISTORY,
    EDIT_WORK_HISTORY,
    UPDATE_USER,
    CREATE_BIO_BATCH,
    FETCH_BIO_BATCH,
    UPDATE_BRIEF,
} from '@/components/onboarding/Actions';
import {
    haveErrors,
    reportGeneralError,
    BIO_NAMES,
    BIO_STATES,
} from '@/components/onboarding/Shared';
import PanelContainer from '@/components/commons/panel_container';
import routes from '@/services/routes';
import { type bioPageProps } from '@/types/globals'; // TODO: THis type belongs elsehwere!
import { BioCandidateStatusEnum, userAtom } from '@/components/with_auth/State';
import Checkbox from '@/components/forms/checkbox';
import FormError from '@/components/forms/FormError';
import WarningCard from '@/components/commons/warning_card';
import { BIO_GEN_TEMPERATURE } from '@/utils/index'; // TODO: This constant belongs elsewhere!

import EntryCard from './EntryCard';
import BioPageHeader from './BioPageHeader';
import Input from './Input';
import DatePicker from './DatePicker';
import styles from './Bio.module.css';
import TextAreaInput from './TextArea';
import customModalTheme from './UploadFileModalTheme';

const fieldNames = ['title', 'organization', 'description', 'startAt', 'endAt', 'id'];

function truncateString(text, maxLength) {
    let result = text;
    if (text && text.length > maxLength) {
        result = `${text.slice(0, maxLength)}...`;
    }
    return result;
}

function ManualJobHistoryPanel({
    isModal,
    isEdit,
    extendContinue,
    openBrief,
}: Omit<bioPageProps, 'overrideBack'>) {
    const formRef = useRef<FormikProps<any>>(null);
    const checkBoxRef = useRef<FormikProps<any>>(null);
    // const router = useRouter();
    const [entries, setEntries] = useState([]);
    const [targetEntry, setTargetEntry] = useState(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [targetId, setTargetId] = useState(null);
    const [showStartPicker, setShowStartPicker] = useState(false);
    const [showPicker, setShowPicker] = useState(true);
    const [startDate, setStartDate] = useState(null);
    const [showEndPicker, setShowEndPicker] = useState(false);
    const [endDate, setEndDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [doneClicked, setDoneClicked] = useState(false);
    const [noDataModal, setNoDataModal] = useState(false);
    const [formError, setFormError] = useState('');

    // TODO(tom) this monitor bioBatch creates state is identical to
    // the one in select-import-method, we may be able to extract it
    // into a hook to reuse time allowing.
    // State to monitor bioBatches

    const [checkBios, setCheckBios] = useState(false);
    const [generateTimeout, setGenerateTimeout] = useState(false);

    const user = useRecoilValue(userAtom);

    const [updateUser] = useMutation(UPDATE_USER);
    const [updateBrief] = useMutation(UPDATE_BRIEF);

    const [
        createBioBatches,
        { error: createBioBatchError, reset: resetCreateBioBatch },
    ] = useMutation(CREATE_BIO_BATCH);

    const [getBioBatches, { data: bioBatchData }] = useLazyQuery(FETCH_BIO_BATCH, {
        // automatically go to the server and poll every 10 seconds
        pollInterval: 10 * 1000,
    });

    useEffect(() => {
        if (!bioBatchData) {
            return;
        }
        const status = bioBatchData.user.bioBatches.map(
            (bio) => bio.status,
        );

        const nodataStatus = status.filter(
            (item) => item === 'nodata',
        );

        if (nodataStatus.length > 0) {
            setCheckBios(false);
            setNoDataModal(true);
            return;
        }

        // get candidate statuses
        const candidatesStatuses = bioBatchData.user.bioBatches.map(
            (bio) => bio.candidates.map((candidate) => candidate.status),
        ).flat();

        // if we have no candidates yet, don't move forward
        if (!candidatesStatuses.length) {
            return;
        }

        // filter for any single success
        const successful = candidatesStatuses.filter(
            (item) => item === BioCandidateStatusEnum.Succeeded,
        );

        if (successful.length > 0) {
            // at least one is successful, go to the next page
            if (isModal && extendContinue) {
                extendContinue('', isEdit);
            } else {
                updateUser({
                    variables: {
                        updateUserInput: {
                            attributes: {
                                onboardingState: BIO_STATES.selectBio,
                            },
                        },
                    },
                });
                router.push(routes.BIO_SELECT_STARTER_BIO);
            }
        }
    }, [bioBatchData, updateUser, isModal, extendContinue, isEdit]);

    useEffect(() => {
        if (createBioBatchError) {
            setFormError('An unexpected error occurred.');
            resetCreateBioBatch();
            setCheckBios(false);
            // TODO(tom) what do we do here if the createBioBatches called errored out
            // if we try again we create a loop the user can't get out of if there's
            // a server error, if we hide the loader at least they can go back to manual entry
        }
    }, [createBioBatchError, resetCreateBioBatch, setCheckBios]);

    // set a timeout if the generate candidates never works
    // so the user doesn't get stuck and can skip ahead
    useEffect(() => {
        // skip this timeout unless we are checking bios
        if (!checkBios) {
            return;
        }
        const timeoutMins = 2 * 60 * 1000;
        // set a timeout if we never get candidates
        const timerId = setTimeout(() => {
            setGenerateTimeout(true);
        }, timeoutMins);

        // eslint-disable-next-line consistent-return
        return () => {
            clearInterval(timerId);
        };
    }, [checkBios, setGenerateTimeout]);

    // dumb hack to reset the Flowbite React Datepicker component that
    // for some weird reason doesn't let you define its value
    // other than on start up....
    // so basically when we need to define its value we re-render it
    useEffect(() => {
        let timer;
        if (!showPicker) {
            timer = setTimeout(() => {
                setShowPicker(true);
            }, 10);
        }

        return () => clearInterval(timer);
    }, [showPicker, setShowPicker]);

    const options = {
        datepickerClassNames: 'relative sm:absolute top-[0px] sm:top-[120px]  left-auto  translate-x-0 ml-[-36px] sm:ml-0',
        maxDate: new Date(Date.now()),
        minDate: new Date('1950-01-01'),
        inputNameProp: 'startAt',
        inputIdProp: 'startAt',
        theme: {
            selected: 'bg-cleanSlate',
            hover: '!text-cleanSlate',
            todayBtn: 'bg-cleanSlate',
            text: 'text-instrument text-cleanSlate',
            input: 'text-instrument text-cleanSlate',
        },
        defaultDate: null,
    };
    const options2 = {
        datepickerClassNames: 'relative sm:absolute top-[0px] sm:top-[120px] translate-x-[-48%] left-[50%] left-auto translate-x-0 ml-[-36px] sm:ml-0',
        maxDate: new Date('2030-01-01'),
        minDate: startDate || new Date('1950-01-01'),
        inputNameProp: 'endAt',
        inputIdProp: 'endAt',
        theme: {
            selected: 'bg-cleanSlate',
            hover: '!text-cleanSlate',
            todayBtn: 'bg-cleanSlate',
            text: 'text-instrument text-cleanSlate',
            input: 'text-instrument text-cleanSlate',
        },
        defaultDate: null,
    };

    const handleAdd = (values, setFieldValue) => {
        const formValues = { ...values, id: entries[targetEntry]?.id };
        if (targetEntry !== 0 && !targetEntry) {
            setEntries((prevState) => [...prevState, formValues]);
        } else {
            const updatedEntries = [...entries];
            updatedEntries[targetEntry] = formValues;
            setEntries(updatedEntries);
            setTargetEntry(null);
        }
        fieldNames.forEach((fieldName) => {
            setFieldValue(fieldName, '');
        });

        setFormError('');
        setSubmitting(false);
        setShowPicker(false);
    };

    const handleAddClick = async () => {
        if (formRef.current) {
            await formRef.current.handleSubmit();
        }
    };

    const handleEdit = (index) => {
        if (entries[index]) {
            setTargetEntry(index);
            if (formRef.current) {
                const form = formRef.current;
                const entry = entries[index];
                form.setFieldValue('title', entry.title);
                form.setFieldValue('organization', entry.organization);
                form.setFieldValue('description', entry.description);
                form.setFieldValue('startAt', entry.startAt);
                form.setFieldValue('endAt', entry.endAt);
                setStartDate(entry.startAt); // sync state with edit
                setEndDate(entry.endAt); // sync state with edit
                setShowPicker(false);
            }
        }
    };

    const [createWorkHistory] = useMutation(CREATE_WORK_HISTORY);
    const [deleteWorkHistory] = useMutation(DELETE_WORK_HISTORY);
    const [editWorkHistory] = useMutation(EDIT_WORK_HISTORY);

    useQuery(FETCH_WORK_HISTORY, {
        variables: {
            id: null,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(),
        onCompleted: onCompletedHandler(null, (value) => {
            setEntries([...entries, ...value.user.workHistoryItems]);
        }),
    });

    const handleRemoveModal = (id, index) => {
        setDeleteModalVisible(true);
        setTargetEntry(index);
        setTargetId(id);
    };

    const handleRemove = (id, index) => {
        const updatedEntries = [...entries];
        if (id) {
            deleteWorkHistory({
                variables: {
                    deleteWorkHistoryItemInput: {
                        id,
                    },
                },
                notifyOnNetworkStatusChange: true,
                fetchPolicy: 'no-cache',
                onError: onErrorHandler((error) => {
                    reportGeneralError(error);
                }),
                onCompleted: onCompletedHandler(() => { }, (data) => {
                    const { errors } = data.deleteWorkHistoryItem;
                    if (haveErrors(errors)) {
                        setFormError(errors);
                    }
                }),
            });
        }
        updatedEntries.splice(index, 1);
        setEntries(updatedEntries);
        setDeleteModalVisible(false);
    };

    const handleContinue = useCallback(async () => {
        entries.forEach((entry) => {
            // only add new values
            if (!entry.id) {
                createWorkHistory({
                    variables: {
                        createWorkHistoryItemInput: {
                            attributes: {
                                description: entry.description,
                                organization: entry.organization,
                                title: entry.title,
                                startAt: entry.startAt,
                                endAt: entry.endAt ? entry.endAt : null,
                            },

                        },
                    },
                    notifyOnNetworkStatusChange: true,
                    fetchPolicy: 'no-cache',
                    onError: onErrorHandler((error) => {
                        reportGeneralError(error);
                    }),
                    onCompleted: onCompletedHandler(() => { }, (data) => {
                        const { errors } = data.createWorkHistoryItem;
                        if (haveErrors(errors)) {
                            setFormError(errors);
                        }
                    }),
                });
            } else {
                editWorkHistory({
                    variables: {
                        updateWorkHistoryItemInput: {
                            attributes: {
                                description: entry.description,
                                organization: entry.organization,
                                title: entry.title,
                                startAt: entry.startAt,
                                endAt: entry.endAt ? entry.endAt : null,
                            },
                            id: entry.id,
                        },
                    },
                    notifyOnNetworkStatusChange: true,
                    fetchPolicy: 'no-cache',
                    onError: onErrorHandler((error) => {
                        reportGeneralError(error);
                    }),
                    onCompleted: onCompletedHandler(() => { }, (data) => {
                        const { errors } = data.updateWorkHistoryItem;
                        if (haveErrors(errors)) {
                            setFormError(errors);
                        }
                    }),
                });
                // edit entry
            }
        });

        // TODO(tom) currently with this implementation, if there's an error saving
        // or updating we'll ignore it and try to create bio batches anyway
        await createBioBatches({
            variables: {
                input: {
                    attributes: {
                        temperature: BIO_GEN_TEMPERATURE,
                    },
                },
            },
        });
        setCheckBios(true);
        await getBioBatches();
    }, [createBioBatches, createWorkHistory, editWorkHistory, entries, getBioBatches, setCheckBios,
        setFormError]);

    const handleBriefUpdate = (attributes, setStatus) => {
        updateBrief({
            variables: {
                input: {
                    id: openBrief
                        ? openBrief.briefIndex
                        : user.briefs[0].id, // initial brief
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, async (data) => {
                setSubmitting(false);
                const { errors } = data.updateBrief;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'brief');
                    return;
                }
                setStatus({ errors: {} });
            }),
        });
    };

    const handleContinueStart = () => {
        if (formRef.current) {
            const form = formRef.current;
            const {
                title, organization,
            } = form.values;

            if (checkBoxRef.current) {
                checkBoxRef.current.handleSubmit();
            }
            if (title && organization && startDate) {
                setSubmitting(true);
                handleAddClick();
            } else if (targetEntry || !entries.length) {
                // Trying to edit OR there are no entries
                // AND there are invalid form values.
                // Set error and return to prevent invalid submission
                setFormError('missing required fields');
                return;
            }
        }

        setDoneClicked(true);
    };

    const skipGenerate = () => {
        if (isModal && extendContinue) {
            extendContinue('', isEdit);
        } else {
            router.push(routes.BIO_EDIT_BIO);
        }
    };
    useEffect(() => {
        if (submitting === false && doneClicked === true) {
            handleContinue();
        }
    }, [handleContinue, doneClicked, submitting, targetId]);

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={1} totalSteps={5} isModal={isModal}>
            <div className="w-full py-0 sm:py-2 px-0 sm:px-5">
                <BioPageHeader
                    header="Describe your career in your own words."
                    subHeader="Add as many or as few jobs as you like. Emphasize your responsibilities and accomplishments. Sixty will draft your official bio from the details you share."
                    subHeaderWidth="max-w-[550px]"
                />
                <Modal dismissible className="p-0 m-0 font-instrument absolute z-[1000] focus:outline-none focus-visible:outline-none focus-visible:ring-0" show={deleteModalVisible} onClose={() => setDeleteModalVisible(false)} position="center" size="xl">
                    <div className="flex flex-col h-[250px] items-center justify-center">
                        <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                            Are you sure you want to delete this job?
                        </h3>
                        <div className="flex justify-center gap-4">
                            <Button color="failure" onClick={() => handleRemove(targetId, targetEntry)}>
                                Yes, I&apos;m sure
                            </Button>
                            <Button color="gray" onClick={() => setDeleteModalVisible(false)}>
                                No, cancel
                            </Button>
                        </div>
                    </div>
                </Modal>
                {
                    formError !== '' && (
                        <div className="w-full my-[20px]">
                            <WarningCard message={formError} />
                        </div>
                    )
                }
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="rounded-lg border border-gray-200 bg-cirrusWhite shadow-md pl-2 sm:pl-10 pr-2 sm:pr-8 py-8 flex flex-col gap-6 w-full md:w-2/3">
                        <div className="flex flex-col gap-2">
                            <Formik
                                innerRef={formRef}
                                onSubmit={(frm, { setFieldValue, setStatus }) => {
                                    setSubmitting(true);
                                    const title = frm.title.trim();
                                    const organization = frm.organization.trim();
                                    const description = frm.description?.trim();
                                    const startAt = startDate
                                        ? new Date(startDate).toISOString()
                                        : null;
                                    const endAt = endDate
                                        ? new Date(endDate).toISOString()
                                        : null;

                                    if (!title || !organization || !startAt) {
                                        // No valid inputs
                                        setSubmitting(false);
                                        setStatus({
                                            errors: {
                                                title: !title && 'Title is required',
                                                organization: !organization && 'Organization is required',
                                                startAt: !startAt && 'Start date is required',
                                            },
                                        });
                                        setFormError('');
                                        return;
                                    }

                                    const attributes = {
                                        title,
                                        organization,
                                        description,
                                        startAt,
                                        endAt,
                                    };

                                    handleAdd(attributes, setFieldValue);
                                    setStatus({
                                        errors: {
                                            title: false,
                                            organization: false,
                                            description: false,
                                            startAt: false,
                                        },
                                    });
                                }}
                                initialValues={{
                                    title: '',
                                    organization: '',
                                    description: '',
                                    startAt: '',
                                    endAt: '',
                                }}
                                initialStatus={{
                                    errors: {
                                        title: false,
                                        organization: false,
                                        description: false,
                                        startAt: false,
                                    },
                                }}
                            >

                                {({ values, status }) => (
                                    <Form>
                                        <div className="flex flex-col sm:flex-row gap-2 w-full pb-2 sm:pb-0">
                                            <div className="sm:pb-5">
                                                <Label
                                                    value="Job title"
                                                    className={styles.labelClassName}
                                                />
                                                <Field
                                                    name="title"
                                                    className="w-full sm:w-[255px]"
                                                    placeholder="Engineering Director"
                                                    component={Input}
                                                    required
                                                />
                                                <FormError error={status.errors.title} />
                                            </div>
                                            <div className="sm:pb-5 w-full">
                                                <Label
                                                    value="Organization"
                                                    className={styles.labelClassName}
                                                />
                                                <Field
                                                    name="organization"
                                                    placeholder="Amenitry Software"
                                                    className="w-full"
                                                    component={Input}
                                                    required
                                                />
                                                <FormError error={status.errors.organization} />
                                            </div>
                                        </div>
                                        <div className="pb-5">
                                            <Label
                                                value="Description of role"
                                                className={styles.labelClassName}
                                            />
                                            <Field
                                                name="description"
                                                placeholder="In January 2023 I joined Amity Software as its Engineering Director. My emphasis on building diverse and equitable teams was a compelling|"
                                                className="w-full resize-none min-h-[105px] text-sm"
                                                component={TextAreaInput}
                                                required
                                            />
                                            <FormError error={status.errors.description} />
                                        </div>
                                        <div className="h-[77px]">
                                            {showPicker && (
                                                <div className="flex flex-col sm:flex-row">
                                                    <div className="pr-0 sm:pr-2 w-full sm:w-1/2">
                                                        <Label
                                                            value="Start date"
                                                            className={styles.labelClassName}
                                                        />
                                                        <Field
                                                            name="startAt"
                                                            component={DatePicker}
                                                            show={showStartPicker}
                                                            options={{
                                                                ...options,
                                                                defaultDate: values.startAt
                                                                    ? new Date(values.startAt)
                                                                    : null,
                                                            }}
                                                            setShow={setShowStartPicker}
                                                            onChange={setStartDate}
                                                            value={values.startAt}
                                                            required
                                                        />
                                                    </div>

                                                    {startDate !== null ? (
                                                        <div className="pl-0 sm:pl-2 mt-4 sm:mt-0 w-full sm:w-1/2">
                                                            <Label
                                                                value="End date"
                                                                className={styles.labelClassName}
                                                            />
                                                            <Field
                                                                name="endAt"
                                                                component={DatePicker}
                                                                show={showEndPicker}
                                                                options={{
                                                                    ...options2,
                                                                    defaultDate: values.endAt
                                                                        ? new Date(values.endAt)
                                                                        : null,
                                                                }}
                                                                setShow={setShowEndPicker}
                                                                onChange={setEndDate}
                                                                clearPicker={() => setEndDate(null)}
                                                                value={values.endAt}
                                                                required
                                                            />
                                                        </div>
                                                    ) : (

                                                        <div className="pt-[13px] pl-0 sm:pl-2 w-full sm:w-1/2">
                                                            <WarningCard
                                                                bkgColor="progressBarBlue"
                                                                message="Select Start date first"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <FormError error={status.errors.startAt} externalStyles="max-w-[230px]" />
                                    </Form>
                                )}
                            </Formik>
                        </div>
                        <div className="block sm:hidden h-[50px]" />
                        <div className="flex flex-col gap-3 justify-between w-full sm:flex-row">
                            <Button
                                color="gray"
                                className="w-full sm:max-w-[396px]"
                                onClick={handleAddClick}
                                isProcessing={submitting}
                                disabled={submitting}
                            >
                                Add another job
                            </Button>
                            <Button
                                color="dark"
                                className="bg-cleanSlate text-base font-medium w-full sm:max-w-[198px]"
                                onClick={handleContinueStart}
                                isProcessing={submitting}
                                disabled={submitting}
                            >
                                I&apos;m done
                            </Button>
                        </div>
                    </div>

                    <div className="w-full md:w-1/3">
                        <div className="mb-8">
                            {/* check box here */}
                            <div className="mt-auto">
                                <Formik
                                    innerRef={checkBoxRef}
                                    onSubmit={(frm, { setStatus }) => {
                                        const attributes = {
                                            displayWorkHistory: frm.displayWorkHistory,
                                        };

                                        handleBriefUpdate(attributes, setStatus);
                                    }}
                                    initialValues={{
                                        displayWorkHistory: false,
                                    }}
                                >
                                    {() => (
                                        <Form>
                                            <Checkbox
                                                disabled={false}
                                                className={`${styles.enabled}`}
                                                tipId="tip"
                                                tipContent="Scan for events starting from the hour the agenda is sent, instead of scanning from midnight."
                                                label="Make career history visible publicly"
                                                name="displayWorkHistory"
                                                description="Show my career history on my public brief"
                                            />
                                        </Form>
                                    )}
                                </Formik>
                            </div>

                        </div>
                        {!!entries.length && (
                            <div>
                                <p className="text-cleanSlate font-instrument text-sm font-medium leading-[21px] mb-3">Previously added</p>
                                <div className="w-full">
                                    {entries.map((entry, index) => (
                                        <EntryCard
                                            jobTitle={truncateString(entry.title, 30)}
                                            jobDescription={truncateString(`${entry.organization ? (`${entry.organization}, `) : ''} ${entry?.startAt?.split('-')[0]} ${entry?.endAt?.length > 0 ? `- ${entry?.endAt?.split('-')[0]}` : ''}`, 30)}
                                            onClickEdit={() => handleEdit(index)}
                                            onClickRemove={
                                                () => handleRemoveModal(entry.id, index)
                                            }
                                            key={entry.title + entry.organization}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {checkBios && (
                <Modal className="absolute z-[1000]" theme={customModalTheme} show={checkBios} position="center" size="sm">
                    <p className="text-[20px] font-bold">Analyzing your data...</p>
                    <p>We&apos;re processing your data. Just a moment...</p>
                    <div className="flex items-center justify-center">
                        <Spinner size="xl" />
                    </div>
                    {generateTimeout
                        && (
                            <>
                                <p>Taking too long?</p>
                                <Button
                                    color="dark"
                                    className="bg-cleanSlate"
                                    onClick={skipGenerate}
                                >
                                    Skip
                                </Button>
                            </>
                        )}
                </Modal>
            )}
            {
                noDataModal && (
                    <Modal className="absolute z-[1000]" theme={customModalTheme} show={noDataModal} position="center" size="sm">
                        <p className="text-[20px] font-bold">No data found</p>
                        <p>
                            We couldn&apos;t extract any useful
                            information from the documents you provided.
                        </p>

                        <p>Skip to manual entry</p>
                        <Button
                            color="dark"
                            className="bg-cleanSlate"
                            onClick={skipGenerate}
                        >
                            Manual Entry
                        </Button>
                    </Modal>
                )
            }
        </PanelContainer>
    );
}

export default ManualJobHistoryPanel;
